import { useCallback, useEffect, useRef, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import useLorebook from "./useLorebook";
const SEARCH_DEBOUNCE_MS = 400;
function buildFetchOptions({ trimmedSearch, append, cursorId, cursorPriority, view, }) {
    // The read mode rides on every request — first page, pagination and search alike — or the list
    // would mix approved rows with the creator's staged edit halfway through a session.
    const viewOption = view ? { view } : {};
    if (trimmedSearch)
        return { search: trimmedSearch, ...viewOption };
    return { lastEntryId: append ? cursorId : undefined, lastSortPriority: append ? cursorPriority : 0, ...viewOption };
}
// The backend flags the pagination cursor entry with is_last_id=true; it is not always the
// positional last element, so resolve the cursor from that flag, not from array order.
function resolvePageCursor(entries, previousSortPriority) {
    const cursorEntry = entries.find((entry) => entry?.is_last_id);
    return {
        hasNextPage: Boolean(cursorEntry),
        lastEntryId: cursorEntry?.id,
        lastSortPriority: cursorEntry ? (cursorEntry.sortPriority ?? 0) : previousSortPriority,
    };
}
/**
 * @param options.view `live` serves the last approved lorebook instead of the creator's staged edit.
 *   Read once per render as a primitive, so callers can pass an inline object without re-triggering
 *   the fetch; changing it mid-session is not supported (the profile and the Edit screen each pick one).
 */
export default function useLorebookEntries(lorebookId, options) {
    const view = options?.view;
    const { getLorebookData } = useLorebook();
    const [stateLorebook, setStateLorebook] = useState(null);
    const [stateEntries, setStateEntries] = useState([]);
    const [stateIsLoadingFirstPage, setStateIsLoadingFirstPage] = useState(true);
    const [stateIsLoadingMore, setStateIsLoadingMore] = useState(false);
    const [stateIsMoreAvailable, setStateIsMoreAvailable] = useState(false);
    const [stateIsErrored, setStateIsErrored] = useState(false);
    const [stateSearchValue, setStateSearchValue] = useState("");
    const refLorebookId = useRef(lorebookId);
    const refLastEntryId = useRef(undefined);
    const refLastSortPriority = useRef(0);
    const refActiveSearch = useRef("");
    const refRequestSeq = useRef(0);
    const refIsFetching = useRef(false);
    const refHasMore = useRef(false);
    // Cumulative optimistic num_entries delta not yet reconciled by an authoritative (unfiltered
    // first-page) response. Kept in a ref, not tied to a single fetch's closure, so it survives when a
    // reloadFromStart refresh is superseded and dropped at the sequence guard — the next winning
    // first-page response then reconciles it instead of the rollback being silently lost.
    const refPendingCountDelta = useRef(0);
    const applyPageResult = useCallback((response, { append, isSearch }) => {
        const entries = response.entries ?? [];
        const cursor = resolvePageCursor(entries, refLastSortPriority.current);
        refLastEntryId.current = cursor.lastEntryId;
        refLastSortPriority.current = cursor.lastSortPriority;
        refHasMore.current = cursor.hasNextPage;
        // Only the initial unpaginated request is the canonical source for lorebook metadata
        // (name, num_entries, tags, etc.). Subsequent pages and search responses carry envelope
        // fields that reflect that subset, so preserve the cached lorebook from the first load.
        if (!append && !isSearch) {
            setStateLorebook(response);
        }
        setStateEntries((previous) => (append ? [...previous, ...entries] : entries));
        setStateIsMoreAvailable(cursor.hasNextPage);
    }, []);
    const fetchPage = useCallback(async ({ search, append }) => {
        const trimmedSearch = search.trim();
        const sequence = ++refRequestSeq.current;
        refActiveSearch.current = trimmedSearch;
        refIsFetching.current = true;
        setStateIsErrored(false);
        const setLoading = append ? setStateIsLoadingMore : setStateIsLoadingFirstPage;
        setLoading(true);
        const fetchOptions = buildFetchOptions({
            trimmedSearch,
            append,
            cursorId: refLastEntryId.current,
            cursorPriority: refLastSortPriority.current,
            view,
        });
        const { data: response, error } = await getLorebookData(lorebookId, fetchOptions);
        // A superseded request must never write state: a newer request owns the outcome (and the
        // reconciliation of any pending optimistic num_entries delta), so writing here would be a stale
        // write. The pending delta is intentionally left untouched for that newer request to reconcile.
        if (sequence !== refRequestSeq.current)
            return;
        // Only an unfiltered first-page request carries the authoritative total, so only it reconciles the
        // optimistic num_entries delta applied by reloadFromStart. Search/append responses leave it pending.
        const shouldReconcileTotal = !append && !trimmedSearch;
        if (error || !response) {
            setStateIsErrored(true);
            // On an append failure keep the cursor and hasMore intact so loadNextPage can re-attempt the
            // same next page; only a first-page failure clears pagination (there is nothing to page from).
            if (!append) {
                refHasMore.current = false;
                setStateIsMoreAvailable(false);
            }
            // The reconciling refresh failed: roll the whole pending delta back so num_entries matches the
            // still-visible (un-refreshed) list. Rolling back the cumulative delta — not just one call's —
            // covers an earlier reloadFromStart whose own refresh was superseded and dropped before this one.
            if (shouldReconcileTotal && refPendingCountDelta.current !== 0) {
                const pendingDelta = refPendingCountDelta.current;
                refPendingCountDelta.current = 0;
                setStateLorebook((previous) => (previous ? { ...previous, num_entries: Math.max(0, previous.num_entries - pendingDelta) } : previous));
            }
        }
        else {
            applyPageResult(response, { append, isSearch: Boolean(trimmedSearch) });
            // A successful unfiltered first page just wrote the authoritative total via applyPageResult,
            // which supersedes any optimistic delta still pending.
            if (shouldReconcileTotal) {
                refPendingCountDelta.current = 0;
            }
        }
        // Clear after applyPageResult has updated refHasMore so a re-render firing onEndReached
        // sees consistent ref state and cannot bypass the guard with a stale closure.
        refIsFetching.current = false;
        setStateIsLoadingFirstPage(false);
        setStateIsLoadingMore(false);
    }, [applyPageResult, getLorebookData, lorebookId, view]);
    const runFirstPage = useCallback((search, { preserveEntries = false } = {}) => {
        refLastEntryId.current = undefined;
        refLastSortPriority.current = 0;
        refHasMore.current = false;
        // Keep the loaded entries on screen while a refresh is in flight so a failed refetch does not
        // blank the list (a successful response replaces them). Callers that must show a clean slate —
        // initial load, lorebook switch, and search — omit this and clear immediately to reveal the skeleton.
        if (!preserveEntries) {
            setStateEntries([]);
        }
        fetchPage({ search, append: false }).catch(() => setStateIsErrored(true));
    }, [fetchPage]);
    const triggerSearchFetch = useDebouncedCallback((next) => runFirstPage(next), SEARCH_DEBOUNCE_MS);
    const setSearchValue = useCallback((next) => {
        setStateSearchValue(next);
        // Mark the active search synchronously (not 400ms later inside fetchPage) so loadNextPage's
        // guard blocks pagination immediately during the debounce window. Without this, an
        // onEndReached firing before the debounced fetch runs would paginate the pre-search dataset.
        refActiveSearch.current = next.trim();
        triggerSearchFetch(next);
    }, [triggerSearchFetch]);
    const loadNextPage = useCallback(() => {
        // refIsFetching and refHasMore are synchronous guards: lists can fire onEndReached multiple
        // times in a single frame before the loading/hasMore state flushes, so reading the refs (not
        // the React state) prevents a stale closure from issuing a duplicate or past-the-end request.
        if (refIsFetching.current || !refHasMore.current)
            return;
        if (refActiveSearch.current)
            return;
        fetchPage({ search: "", append: true }).catch(() => setStateIsErrored(true));
    }, [fetchPage]);
    const reload = useCallback(() => runFirstPage(stateSearchValue), [runFirstPage, stateSearchValue]);
    // Full refresh from the unfiltered first page. Clears any active search so the refetch is a
    // non-search request, which is the only response that refreshes cached lorebook metadata
    // (num_entries). Used after an entry create/delete so counts, the entry limit, and the
    // "at least one entry" publish gate stay correct even when a search filter was applied.
    // entryCountDelta optimistically reconciles num_entries for the window before the refetch lands, so
    // publish gates and the limit are correct immediately. The delta is accumulated into
    // refPendingCountDelta rather than reconciled by this fetch's own handler: whichever unfiltered
    // first-page request ultimately wins reconciles it (authoritative total on success, full rollback on
    // failure), so a superseded/dropped refresh can no longer strand the count at a wrong value.
    const reloadFromStart = useCallback((entryCountDelta = 0) => {
        triggerSearchFetch.cancel();
        refActiveSearch.current = "";
        setStateSearchValue("");
        if (entryCountDelta !== 0) {
            refPendingCountDelta.current += entryCountDelta;
            setStateLorebook((previous) => (previous ? { ...previous, num_entries: Math.max(0, previous.num_entries + entryCountDelta) } : previous));
        }
        // Preserve the visible list during the refetch: a create/delete refresh should not blank the
        // entries if the network call fails; the response replaces them on success.
        runFirstPage("", { preserveEntries: true });
    }, [runFirstPage, triggerSearchFetch]);
    // Clear the previous load's error synchronously during render the moment lorebookId changes.
    // The effect below (which clears metadata and starts the new fetch) runs only AFTER this render
    // commits, so relying on it would leave one committed render where the new id is active but hasError
    // is still true from the old failed load. A consumer that toasts on `hasError && !lorebook` would
    // then fire against that stale error while the new fetch is only just starting. Resetting here makes
    // React discard the stale render and re-render with hasError=false before committing.
    if (refLorebookId.current !== lorebookId) {
        refLorebookId.current = lorebookId;
        setStateIsErrored(false);
        // Drop any optimistic delta from the previous lorebook so it cannot be reconciled against the new
        // lorebook's total by a subsequent first-page response.
        refPendingCountDelta.current = 0;
    }
    useEffect(() => {
        // Cancel any pending debounced search and reset the query so a stale search from the previous
        // lorebook cannot fire after this clean load and apply the wrong query to the new lorebook.
        triggerSearchFetch.cancel();
        refActiveSearch.current = "";
        setStateSearchValue("");
        // Clear the cached metadata so the previous lorebook's header does not render while the new
        // lorebook loads; this also re-arms the consumer's (isLoadingFirstPage && !lorebook) guard.
        setStateLorebook(null);
        // No id yet (the create flow, before the lorebook exists) — there is nothing to fetch, and
        // requesting an empty id would 404. Settle into a loaded-but-empty state instead.
        if (!lorebookId) {
            setStateEntries([]);
            setStateIsMoreAvailable(false);
            setStateIsLoadingFirstPage(false);
            return;
        }
        runFirstPage("");
    }, [lorebookId, runFirstPage, triggerSearchFetch]);
    return {
        lorebook: stateLorebook,
        entries: stateEntries,
        isLoadingFirstPage: stateIsLoadingFirstPage,
        isLoadingMore: stateIsLoadingMore,
        hasMore: stateIsMoreAvailable,
        hasError: stateIsErrored,
        searchValue: stateSearchValue,
        setSearchValue,
        loadNextPage,
        reload,
        reloadFromStart,
    };
}
